import { GapConstraint, Provenance, VarId } from '../types'
import { GraphResult } from './orderGraph'

const MAX_BRANCHES = 256

export type GapObligation =
  | {
      kind: 'gapOrder'
      biggerAbs: [VarId, VarId]
      smallerAbs: [VarId, VarId]
      provenance: Provenance
    }
  | {
      kind: 'gapVsConst'
      pair: [VarId, VarId]
      rel: '=' | '>' | '<'
      constant: number
      provenance: Provenance
    }
  | {
      kind: 'sameSign'
      a: [VarId, VarId]
      b: [VarId, VarId]
      provenance: Provenance
    }

export interface GapResolution {
  ok: boolean
  conflict?: { provenance: Provenance[] }
  unverified: boolean
  obligations: GapObligation[]
}

type Sign = 1 | -1

const pairKey = (minuend: VarId, subtrahend: VarId): string => `${minuend}→${subtrahend}`

/**
 * Resolves the sign structure of |gap| constraints (converging/diverging/
 * changed) per spec §7.4: signs are derived from graph entailment first; only
 * the residual unknowns are case-split, capped at MAX_BRANCHES. All branches
 * failing proves the gap set contradictory.
 */
export const resolveGaps = (gaps: GapConstraint[], graph: GraphResult): GapResolution => {
  if (gaps.length === 0) return { ok: true, unverified: false, obligations: [] }

  // Collect every pair and derive entailed signs.
  const knownSigns = new Map<string, Sign>()
  const unknownPairs: Array<[VarId, VarId]> = []
  const seenPairs = new Set<string>()
  const registerPair = (minuend?: VarId, subtrahend?: VarId): void => {
    if (!minuend || !subtrahend) return
    const key = pairKey(minuend, subtrahend)
    if (seenPairs.has(key)) return
    seenPairs.add(key)
    if (graph.entails(minuend, subtrahend)) knownSigns.set(key, 1)
    else if (graph.entails(subtrahend, minuend)) knownSigns.set(key, -1)
    else unknownPairs.push([minuend, subtrahend])
  }
  gaps.forEach((gap) => {
    registerPair(gap.aMinuend, gap.aSubtrahend)
    registerPair(gap.bMinuend, gap.bSubtrahend)
  })

  const branchCount = 2 ** unknownPairs.length
  const unverified = branchCount > MAX_BRANCHES
  const branchesToTry = unverified ? 1 : branchCount

  const tryBranch = (branch: number): GapObligation[] | { failed: Provenance[] } => {
    const signs = new Map(knownSigns)
    unknownPairs.forEach(([minuend, subtrahend], i) => {
      // eslint-disable-next-line no-bitwise
      signs.set(pairKey(minuend, subtrahend), ((branch >> i) & 1) === 0 ? 1 : -1)
    })
    const obligations: GapObligation[] = []
    const orderKeys = new Map<
      string,
      { direction: 'aBigger' | 'bBigger'; provenance: Provenance }
    >()

    for (const gap of gaps) {
      const aKey = pairKey(gap.aMinuend, gap.aSubtrahend)
      if (gap.kind === 'changedCmp') {
        if (gap.constant === undefined || Number.isNaN(gap.constant) || !gap.rel) continue
        obligations.push({
          kind: 'gapVsConst',
          pair: [gap.aMinuend, gap.aSubtrahend],
          rel: gap.rel,
          constant: gap.constant,
          provenance: gap.provenance,
        })
        continue
      }
      if (!gap.bMinuend || !gap.bSubtrahend) continue
      const bKey = pairKey(gap.bMinuend, gap.bSubtrahend)
      const signA = signs.get(aKey) as Sign
      const signB = signs.get(bKey) as Sign
      if (gap.sameSignPairs && signA !== signB) {
        return { failed: [gap.provenance] }
      }
      const combinedKey = `${aKey}|${bKey}`
      const direction = gap.kind === 'converging' ? 'bBigger' : 'aBigger'
      const existing = orderKeys.get(combinedKey)
      if (existing && existing.direction !== direction) {
        return { failed: [existing.provenance, gap.provenance] }
      }
      orderKeys.set(combinedKey, { direction, provenance: gap.provenance })
      obligations.push({
        kind: 'gapOrder',
        biggerAbs:
          direction === 'bBigger'
            ? [gap.bMinuend, gap.bSubtrahend]
            : [gap.aMinuend, gap.aSubtrahend],
        smallerAbs:
          direction === 'bBigger'
            ? [gap.aMinuend, gap.aSubtrahend]
            : [gap.bMinuend, gap.bSubtrahend],
        provenance: gap.provenance,
      })
      if (gap.sameSignPairs) {
        obligations.push({
          kind: 'sameSign',
          a: [gap.aMinuend, gap.aSubtrahend],
          b: [gap.bMinuend, gap.bSubtrahend],
          provenance: gap.provenance,
        })
      }
    }
    return obligations
  }

  const failures: Provenance[] = []
  for (let branch = 0; branch < branchesToTry; branch += 1) {
    const outcome = tryBranch(branch)
    if (Array.isArray(outcome)) {
      return { ok: true, unverified, obligations: outcome }
    }
    outcome.failed.forEach((provenance) => failures.push(provenance))
  }

  if (unverified) {
    // Best-effort branch also failed; report unverified rather than a hard
    // conflict we have not proven.
    return { ok: true, unverified: true, obligations: [] }
  }

  const uniqueFailures = failures.filter(
    (provenance, i) =>
      failures.findIndex(
        (other) =>
          other.conditionId === provenance.conditionId && other.invariant === provenance.invariant
      ) === i
  )
  return { ok: false, unverified: false, conflict: { provenance: uniqueFailures }, obligations: [] }
}
